import React, { useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter, type Route } from "@/router/Router";
import { useCatalog } from "@/state/CatalogContext";
import { useCategories, categoryLabel } from "@/lib/categories";
import { fetchPosts, mediaUrl } from "@/lib/api";
import { formatUzs } from "@/lib/format";
import { matchScore } from "@/lib/smartSearch";
import type { PostSummary } from "@/types";

const RECENT_KEY = "kms.recentSearch";
const loadRecent = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
};
const saveRecent = (q: string) => {
  try {
    const list = [q, ...loadRecent().filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
};

type Item = { key: string; kind: "product" | "category" | "post" | "action"; title: string; sub?: string; img?: string; price?: string; route: Route };

const SEARCH_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

/** Ctrl+K search window of the desktop site: products, categories and articles as you type. */
export function SearchPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { products } = useCatalog();
  const categories = useCategories();
  const [q, setQ] = useState("");
  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setActive(0);
    window.setTimeout(() => input.current?.focus(), 30);
    if (posts.length === 0) fetchPosts({ limit: 40 }).then(setPosts).catch(() => setPosts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locale]);

  const items = useMemo<Item[]>(() => {
    const query = q.trim();
    if (!query) {
      const popular = products.filter((p) => p.tags?.includes("bestseller")).concat(products).filter((p, i, a) => a.indexOf(p) === i).slice(0, 4);
      return [
        ...popular.map<Item>((p) => ({ key: `p-${p.id}`, kind: "product", title: p.name, sub: categoryLabel(p, t), img: p.images[0], price: p.variants[0]?.priceUzs ? formatUzs(p.variants[0].priceUzs, locale) : undefined, route: { name: "product", slug: p.slug } })),
        { key: "a-flavors", kind: "action", title: t("desktop.fwTitle"), sub: t("desktop.fwCardText"), route: { name: "flavors" } },
      ];
    }
    const prods = products
      .map((p) => ({ p, s: matchScore(query, p.name, p.brand, categoryLabel(p, t), p.shortDescription, ...(p.flavor?.notes ?? []).map((n) => t(`flavor.notes.${n}`)), ...Object.values(p.attributes ?? {})) * 2 + matchScore(query, p.name) * 3 }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 6)
      .map<Item>(({ p }) => ({ key: `p-${p.id}`, kind: "product", title: p.name, sub: categoryLabel(p, t), img: p.images[0], price: p.variants[0]?.priceUzs ? formatUzs(p.variants[0].priceUzs, locale) : p.orderMode === "under_order" ? t("catalog.onRequest") : undefined, route: { name: "product", slug: p.slug } }));
    const cats = (categories ?? [])
      .filter((c) => matchScore(query, c.name) > 0)
      .slice(0, 4)
      .map<Item>((c) => ({ key: `c-${c.id}`, kind: "category", title: c.name, route: { name: "catalog", category: c.slug } }));
    const arts = posts
      .map((p) => ({ p, s: matchScore(query, p.title, p.excerpt) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 3)
      .map<Item>(({ p }) => ({ key: `b-${p.id}`, kind: "post", title: p.title, sub: t(`posts.tabs.${p.type}`), img: p.coverImage ?? undefined, route: { name: "post", slug: p.slug } }));
    return [...prods, ...cats, ...arts, { key: "a-all", kind: "action", title: t("desktop.searchAll", { q: query }), route: { name: "catalog", q: query } }];
  }, [q, products, categories, posts, t, locale]);

  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    list.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;

  const go = (it: Item) => {
    if (q.trim()) saveRecent(q.trim());
    onClose();
    navigate(it.route);
  };
  const recent = q.trim() ? [] : loadRecent();
  const label = { product: t("desktop.searchProducts"), category: t("desktop.searchCategories"), post: t("desktop.searchArticles"), action: "" } as const;
  let lastKind = "";

  return (
    <div className="sp-overlay" onMouseDown={onClose}>
      <div className="sp" role="dialog" aria-modal="true" aria-label={t("desktop.search")} onMouseDown={(e) => e.stopPropagation()}>
        <div className="sp-in">
          {SEARCH_ICON}
          <input
            ref={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("desktop.searchHint")}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((i) => Math.min(items.length - 1, i + 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((i) => Math.max(0, i - 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                const it = items[active];
                if (it) go(it);
              } else if (e.key === "Escape") onClose();
            }}
          />
          <kbd>Esc</kbd>
        </div>
        {recent.length > 0 && (
          <div className="sp-recent">
            <span>{t("desktop.searchRecent")}</span>
            {recent.map((r) => (
              <button key={r} onClick={() => setQ(r)}>{r}</button>
            ))}
          </div>
        )}
        <div className="sp-list" ref={list}>
          {!q.trim() && <div className="sp-sec">{t("desktop.searchPopular")}</div>}
          {items.length === 1 && q.trim() && <div className="sp-none">{t("desktop.searchNone")}</div>}
          {items.map((it, i) => {
            const head = q.trim() && it.kind !== lastKind && label[it.kind] ? <div key={`h-${it.kind}`} className="sp-sec">{label[it.kind]}</div> : null;
            lastKind = it.kind;
            return (
              <React.Fragment key={it.key}>
                {head}
                <button className="sp-item" data-kind={it.kind} data-idx={i} data-active={i === active} onMouseEnter={() => setActive(i)} onClick={() => go(it)}>
                  <span className="sp-item__img">{it.img ? <img src={mediaUrl(it.img, 200)} alt="" /> : it.kind === "category" ? "▦" : it.kind === "action" ? "→" : "✎"}</span>
                  <span className="sp-item__text">
                    <b>{it.title}</b>
                    {it.sub && <small>{it.sub}</small>}
                  </span>
                  {it.price && <em>{it.price}</em>}
                </button>
              </React.Fragment>
            );
          })}
        </div>
        <div className="sp-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> {t("desktop.searchMove")}</span>
          <span><kbd>Enter</kbd> {t("desktop.searchOpen")}</span>
          <span><kbd>Ctrl</kbd><kbd>K</kbd> {t("desktop.searchAnywhere")}</span>
        </div>
      </div>
    </div>
  );
}
